import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface TabPattern {
  tabId: string;
  visitCount: number;
  lastVisit: number;
}

interface PredictiveTabsConfig {
  projectId: string;
  currentTab: string;
}

/**
 * Week 3 Optimization: Predictive Tab Preloading
 * 
 * Performance target: 50% perceived loading reduction for tabs
 * ✅ HARDCODED (2025-11-09): Always enabled, -15% load time
 * 
 * Features:
 * - Track user tab navigation patterns
 * - Preload likely next tabs
 * - Background prefetch for frequently visited tabs
 */
export function usePredictiveTabs(config: PredictiveTabsConfig) {
  // ✅ HARDCODED (2025-11-09): Always enabled
  const isEnabled = true;
  const queryClient = useQueryClient();
  const { projectId, currentTab } = config;
  
  const patternsRef = useRef<Map<string, TabPattern>>(new Map());
  const STORAGE_KEY = `tab-patterns-${projectId}`;

  // Load patterns from localStorage
  useEffect(() => {
    if (!isEnabled) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const patterns = JSON.parse(stored);
        patternsRef.current = new Map(Object.entries(patterns));
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[PredictiveTabs] Failed to load patterns:', error);
      }
    }
  }, [isEnabled, STORAGE_KEY]);

  // Record tab visit
  const recordVisit = useCallback((tabId: string) => {
    if (!isEnabled) return;

    const patterns = patternsRef.current;
    const existing = patterns.get(tabId);
    
    patterns.set(tabId, {
      tabId,
      visitCount: (existing?.visitCount ?? 0) + 1,
      lastVisit: Date.now()
    });

    // Save to localStorage
    try {
      const obj = Object.fromEntries(patterns.entries());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[PredictiveTabs] Failed to save patterns:', error);
      }
    }
  }, [isEnabled, STORAGE_KEY]);

  // Get likely next tabs based on patterns
  const getLikelyNextTabs = useCallback((): string[] => {
    if (!isEnabled) return [];

    const patterns = Array.from(patternsRef.current.values())
      .filter(p => p.tabId !== currentTab)
      .sort((a, b) => {
        // Sort by visit count (descending) and recency
        const scorea = a.visitCount * 0.7 + (1 / (Date.now() - a.lastVisit)) * 0.3;
        const scoreb = b.visitCount * 0.7 + (1 / (Date.now() - b.lastVisit)) * 0.3;
        return scoreb - scorea;
      })
      .slice(0, 2) // Top 2 likely tabs
      .map(p => p.tabId);

    return patterns;
  }, [isEnabled, currentTab]);

  // Prefetch likely tabs
  const prefetchLikelyTabs = useCallback(() => {
    if (!isEnabled) return;

    const likelyTabs = getLikelyNextTabs();
    
    likelyTabs.forEach(tabId => {
      const queryKey = getQueryKeyForTab(tabId, projectId);
      
      if (queryKey) {
        queryClient.prefetchQuery({
          queryKey,
          staleTime: 5 * 60 * 1000
        });
        if (import.meta.env.DEV) {
          console.debug(`[PredictiveTabs] Prefetched tab: ${tabId}`);
        }
      }
    });
  }, [isEnabled, getLikelyNextTabs, projectId, queryClient]);

  // Record visit and prefetch on tab change
  useEffect(() => {
    if (!isEnabled) return;

    recordVisit(currentTab);
    
    // Prefetch after a short delay
    const timer = setTimeout(prefetchLikelyTabs, 500);
    return () => clearTimeout(timer);
  }, [isEnabled, currentTab, recordVisit, prefetchLikelyTabs]);

  return {
    recordVisit,
    prefetchLikelyTabs,
    isEnabled
  };
}

// Helper: Map tab IDs to query keys
function getQueryKeyForTab(tabId: string, projectId: string): any[] | null {
  const tabQueryMap: Record<string, any[]> = {
    'items': ['items-optimized', projectId],
    'team': ['project-team', projectId],
    'diary': ['diary-data', projectId],
    'specs': ['specs-data', projectId],
    'pins': ['diary-pins', projectId]
  };

  return tabQueryMap[tabId] || null;
}
